import axios from "axios";
import { API_BASE_URL } from "../config";

// Open-ended questions are marked by AI on the server against an authored mark scheme.
// The browser never receives that mark scheme — it sends the answer and gets a verdict.

export const isProbabilistic = (question) =>
  question?.grading_mode === "probabilistic";

// Three outcomes, derived from the marks so practice mode (which records nothing and
// therefore returns no last_result) resolves exactly like a graded attempt.
export const OUTCOME = {
  CORRECT: "correct",
  PARTIAL: "partial",
  INCORRECT: "incorrect",
};

export const outcomeForMarks = (marksAwarded, maxMarks) => {
  const awarded = Number(marksAwarded) || 0;
  const max = Number(maxMarks) || 0;
  if (max > 0 && awarded >= max) return OUTCOME.CORRECT;
  return awarded > 0 ? OUTCOME.PARTIAL : OUTCOME.INCORRECT;
};

// Something went wrong on our side or upstream — the student's answer was never
// judged, so offering "try again" is honest. Content/config errors are not retryable:
// the question itself needs fixing and retrying would fail identically.
const RETRYABLE_ERROR_CODES = new Set([
  "timeout",
  "api_error",
  "response_truncated",
  "malformed_response",
  "network_error",
]);

const FALLBACK_MESSAGE =
  "We couldn't mark your answer just now. Your answer has been kept — please try again.";

/**
 * Grade one open-ended answer.
 *
 * @param {number|string} questionId
 * @param {string}  studentAnswer
 * @param {boolean} practice  true → grade without recording progress
 * @param {boolean} usedHelp  student opened the explanation before answering; sent with
 *                            the attempt so the two can never disagree
 * @returns {Promise<{ok: boolean, ...}>} never throws; failures come back as ok:false
 */
export async function gradeProbabilisticResponse({
  questionId,
  studentAnswer,
  practice = false,
  usedHelp = null,
}) {
  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/api/questions/${questionId}/probabilistic_grading`,
      {
        student_answer: studentAnswer ?? "",
        practice,
        ...(usedHelp === null ? {} : { used_help: usedHelp }),
      }
    );

    return {
      ok: true,
      marksAwarded: data.marks_awarded,
      maxMarks: data.max_marks,
      criteria: Array.isArray(data.criteria) ? data.criteria : [],
      feedback: data.feedback ?? "",
      lastResult: data.last_result ?? null,
      outcome: outcomeForMarks(data.marks_awarded, data.max_marks),
    };
  } catch (error) {
    const body = error?.response?.data ?? {};
    const errorCode = body.error_code ?? "network_error";

    return {
      ok: false,
      errorCode,
      retryable: RETRYABLE_ERROR_CODES.has(errorCode),
      message: FALLBACK_MESSAGE,
    };
  }
}
